import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface LeadFiltersProps {
  dateFrom: Date | null;
  dateTo: Date | null;
  practiceType: string;
  consultationFilter: string;
  statusFilter: string;
  onDateFromChange: (date: Date | null) => void;
  onDateToChange: (date: Date | null) => void;
  onPracticeTypeChange: (value: string) => void;
  onConsultationFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onReset: () => void;
}

export const LeadFilters = ({
  dateFrom,
  dateTo,
  practiceType,
  consultationFilter,
  statusFilter,
  onDateFromChange,
  onDateToChange,
  onPracticeTypeChange,
  onConsultationFilterChange,
  onStatusFilterChange,
  onReset,
}: LeadFiltersProps) => {
  const quickDatePresets = [
    { label: 'Heute', days: 0 },
    { label: 'Diese Woche', days: 7 },
    { label: 'Letzten 30 Tage', days: 30 },
    { label: 'Letzten 90 Tage', days: 90 },
  ];

  const applyQuickDate = (days: number) => {
    const to = new Date();
    const from = new Date();
    if (days === 0) {
      from.setHours(0, 0, 0, 0);
    } else {
      from.setDate(from.getDate() - days);
    }
    onDateFromChange(from);
    onDateToChange(to);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {quickDatePresets.map((preset) => (
          <Button
            key={preset.label}
            variant="outline"
            size="sm"
            onClick={() => applyQuickDate(preset.days)}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-4">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'justify-start text-left font-normal',
                !dateFrom && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateFrom ? format(dateFrom, 'PPP', { locale: de }) : 'Von Datum'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={dateFrom || undefined}
              onSelect={(date) => onDateFromChange(date || null)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'justify-start text-left font-normal',
                !dateTo && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateTo ? format(dateTo, 'PPP', { locale: de }) : 'Bis Datum'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={dateTo || undefined}
              onSelect={(date) => onDateToChange(date || null)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Select value={practiceType} onValueChange={onPracticeTypeChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Praxistyp" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Praxistypen</SelectItem>
            <SelectItem value="hausarzt">Hausarzt</SelectItem>
            <SelectItem value="zahnarzt">Zahnarzt</SelectItem>
            <SelectItem value="facharzt">Facharzt</SelectItem>
            <SelectItem value="mvz">MVZ</SelectItem>
            <SelectItem value="klinik">Klinik</SelectItem>
          </SelectContent>
        </Select>

        <Select value={consultationFilter} onValueChange={onConsultationFilterChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Beratungswunsch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="yes">Mit Beratungswunsch</SelectItem>
            <SelectItem value="no">Ohne Beratungswunsch</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="new">🔵 Neu</SelectItem>
            <SelectItem value="contacted">🟡 Kontaktiert</SelectItem>
            <SelectItem value="qualified">🟢 Qualifiziert</SelectItem>
            <SelectItem value="converted">✅ Konvertiert</SelectItem>
            <SelectItem value="lost">⚪ Verloren</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={onReset} className="gap-2">
          <X className="w-4 h-4" />
          Filter zurücksetzen
        </Button>
      </div>
    </div>
  );
};
